"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useEditor } from "./EditorContext";

interface GuardContext {
  guardedNavigate: (href: string) => void;
  guardedAction: (action: () => void) => void;
  saveRef: React.MutableRefObject<(() => Promise<void>) | null>;
  readOnlyRef: React.MutableRefObject<boolean>;
}

const defaultSaveRef = { current: null } as React.MutableRefObject<(() => Promise<void>) | null>;
const defaultReadOnlyRef = { current: false } as React.MutableRefObject<boolean>;

const Ctx = React.createContext<GuardContext>({
  guardedNavigate: () => {},
  guardedAction: (action) => action(),
  saveRef: defaultSaveRef,
  readOnlyRef: defaultReadOnlyRef,
});

export function useUnsavedGuard() {
  return React.useContext(Ctx);
}

export function UnsavedChangesGuard({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const editor = useEditor();
  const t = useTranslations("editor");
  const saveRef = React.useRef<(() => Promise<void>) | null>(null);
  const readOnlyRef = React.useRef(false);
  const [pendingAction, setPendingAction] = React.useState<(() => void) | null>(null);
  const [saving, setSaving] = React.useState(false);

  const guardedAction = React.useCallback(
    (action: () => void) => {
      if (editor?.isDirty && !readOnlyRef.current) {
        setPendingAction(() => action);
      } else {
        action();
      }
    },
    [editor?.isDirty],
  );

  const guardedNavigate = React.useCallback(
    (href: string) => {
      guardedAction(() => router.push(href));
    },
    [guardedAction, router],
  );

  const proceed = React.useCallback(() => {
    const action = pendingAction;
    setPendingAction(null);
    setSaving(false);
    if (action) action();
  }, [pendingAction]);

  const handleSave = React.useCallback(async () => {
    if (!saveRef.current) {
      proceed();
      return;
    }
    setSaving(true);
    try {
      await saveRef.current();
      proceed();
    } catch {
      setSaving(false);
    }
  }, [proceed]);

  const handleDiscard = React.useCallback(() => {
    proceed();
  }, [proceed]);

  const handleCancel = React.useCallback(() => {
    setPendingAction(null);
    setSaving(false);
  }, []);

  React.useEffect(() => {
    if (!editor?.isDirty || readOnlyRef.current) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [editor?.isDirty]);

  const value = React.useMemo(
    () => ({ guardedNavigate, guardedAction, saveRef, readOnlyRef }),
    [guardedNavigate, guardedAction],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <AlertDialog open={pendingAction !== null} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("unsavedTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("unsavedDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel disabled={saving}>{t("unsavedCancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDiscard(); }}
              className="bg-raise text-white hover:bg-raise/80"
            >
              {t("unsavedDiscard")}
            </AlertDialogAction>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleSave(); }}
              disabled={saving}
            >
              {saving ? t("unsavedSaving") : t("unsavedSave")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Ctx.Provider>
  );
}
